// SPDX-License-Identifier: Apache-2.0

import React, { useContext, useEffect, useState } from "react";
import {
  fetchKey,
  fetchPublic,
  localStorageKeyName,
  SepoliaChain,
  SepoliaChainReadOnly,
} from "../client";
import { AppContext } from "../AppContext";
import { useParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import { CnftDisplay } from "../components";
import {
  getMerged,
  getPublic,
  MergedMetadata,
  PublicMetadata,
  StoredMetadata,
} from "../client/Metadata";
import { importRootKey } from "../client/Crypto";

interface Data {
  // Metadata as it was stored
  raw: StoredMetadata;
  // Processed metadata
  metadata: PublicMetadata | MergedMetadata;
  owner: string;
  // Needed because we can't export from CryptoKey.
  key_str?: string;
  key?: CryptoKey;
}

export function ViewNFTPage() {
  // Get token identifier from URL
  const params = useParams();
  const collection = params.collection!;
  const tokenId = BigInt(params.tokenId!);

  const ctx = useContext(AppContext);
  const [data, setData] = useState<Data | undefined>();

  const localStorageKey = localStorageKeyName(collection, tokenId);

  // Fetch the (public) metadata on page load
  //
  // TODO: We probably want to display some loading screen while fetching
  useEffect(() => {
    (async () => {
      const chain = new SepoliaChainReadOnly(ctx.provider);
      console.log("Fetching metadata");
      const nftData = await fetchPublic(collection!, tokenId, chain);
      // TODO: Handle non-cnft metadata gracefully
      const metadata = nftData.metadata as StoredMetadata;

      const keyB64 = localStorage.getItem(localStorageKey);

      if (keyB64 === null) {
        setData({
          raw: metadata,
          // TODO: Find a way to avoid this casting/type assumption
          metadata: getPublic(metadata),
          owner: nftData.owner,
        });
        return;
      }

      // Attempt decryption if we already have a key.
      // TODO: Gracefully handle the case where the key is outdated (causing decryption to fail)
      console.log("Decrypting with key from localStorage");
      if (keyB64 && metadata.confidential !== undefined) {
        const key = await importRootKey(Uint8Array.fromBase64(keyB64));
        setData({
          raw: metadata,
          metadata: await getMerged(metadata, key, false, true),
          owner: nftData.owner,
          key_str: keyB64,
          key,
        });
      }
    })().catch((e) => console.error("Fetching metadata failed", e));
  }, [tokenId, ctx.provider]);

  if (!data) {
    return "Loading";
  }

  const isOwner = ctx.accounts && ctx.accounts[0] === data.owner;
  const keyID = data.metadata.confidential?.key_id;

  // Asks the user to authenticate against the keyserver and decrypts the metadata.
  const fetchDecryptionKey = async () => {
    if (keyID !== undefined) {
      const chain = new SepoliaChain(await ctx.provider.getSigner());
      const key_bytes = await fetchKey(tokenId, chain, keyID);
      const key = await importRootKey(new Uint8Array(key_bytes));
      console.log("Decrypting with key from Keyserver");
      setData({
        raw: data.raw,
        metadata: await getMerged(data.raw, key, false, true),
        owner: data.owner,
        key_str: new Uint8Array(key_bytes).toBase64(),
        key,
      });
    } else {
      console.warn("fetchDecryptionKey called on non-cNFT");
    }
  };

  // Messge/Button to display on the top
  //
  // TODO: Add message for the case where the NFT is minted/you own it, but the
  // block isn't finalized, yet.
  let msg;
  if (!data.metadata.confidential || data.key_str !== undefined) {
    msg = "";
  } else if (!ctx.accounts) {
    msg = "CONNECT TO METAMASK to show confidential data if you own this NFT";
  } else if (!isOwner) {
    msg = "NOT OWNER => Only showing public information";
  } else {
    msg = (
      <>
        You own this NFT, but don't have the decryption key yet.
        <Button onClick={fetchDecryptionKey}>Fetch decryption key</Button>
      </>
    );
  }

  return (
    <div>
      {msg}

      <CnftDisplay
        collection={collection}
        tokenId={tokenId}
        metadata={data.metadata}
        decryptionKey={data?.key_str}
        owner={data.owner}
      />
    </div>
  );
}
